/**
 * Shared action resolution pipeline.
 *
 * P0-Critical: Forms and tables must use the same authoritative resolution
 * pipeline for action visibility, availability, disabled state, and permission
 * checks.  This module provides the canonical `resolveAction` function that
 * both form and table renderers consume.
 */
import type {
  ActionDescriptor,
  TranslationKey,
  FormFieldId,
  PayloadBuilderId,
  FeatureAvailability,
  CommandDescriptorId,
  RowActionEligibility,
} from '@finance/contracts';
import { CANONICAL_OPERATIONS_REGISTRY } from '@finance/contracts/operations';
import { evaluateCondition, type EvaluationContext } from '../conditions/evaluate-condition';

export interface ResolveActionRuntime {
  isDirty?: boolean;
  isValid?: boolean;
  hasPermission?: (perm: string) => boolean;
  isPending?: boolean;
}

export interface ResolvedActionDescriptor {
  action: ActionDescriptor;
  isFormSubmit: boolean;
  commandId: CommandDescriptorId | undefined;
  visible: boolean;
  disabled: boolean;
  disabledReasonKey?: TranslationKey;
  available: boolean;
  validatesForm: boolean;
  includeFields?: FormFieldId[];
  excludeFields?: FormFieldId[];
  payloadBuilderId?: PayloadBuilderId;
  resetBehavior?: 'none' | 'toSubmittedValues' | 'toServerValues' | 'toInitialValues';
  order: number;
  group: 'primary' | 'secondary' | 'tertiary' | 'danger' | 'navigation';
  alignment: 'start' | 'end';
}

export type ResolvedAction = ResolvedActionDescriptor;

export interface ResolveActionOptions {
  /** The action descriptor to resolve. */
  action: ActionDescriptor;
  /** Evaluation context (permissions, modules, etc.). */
  ctx?: EvaluationContext;
  /** Runtime data record (form values or row data). */
  data?: Record<string, unknown>;
  /** Whether the form is in view mode (suppresses command actions). */
  formMode?: string;
  /** Whether an operation is pending. */
  isPending?: boolean;
  /** Runtime state (dirty, valid, permission checker). */
  runtime?: ResolveActionRuntime;
  /** Default command ID to use when the action doesn't specify one. */
  defaultCommandId?: CommandDescriptorId;
  /** Default include fields from the form submission descriptor. */
  defaultIncludeFields?: FormFieldId[];
  /** Default exclude fields from the form submission descriptor. */
  defaultExcludeFields?: FormFieldId[];
  /** Default payload builder ID from the form submission descriptor. */
  defaultPayloadBuilderId?: PayloadBuilderId;
}

/**
 * Canonical action resolution.
 *
 * Determines visibility, availability, disabled state, and submit configuration
 * for an action descriptor.  Used by both form and table renderers.
 */
export function resolveAction(options: ResolveActionOptions): ResolvedAction {
  const {
    action,
    ctx,
    data,
    formMode,
    isPending,
    runtime,
    defaultCommandId,
    defaultIncludeFields,
    defaultExcludeFields,
    defaultPayloadBuilderId,
  } = options;

  const isFormSubmit =
    formMode !== 'view' &&
    (action.submitBehavior === 'form_submit' ||
      (action.submitBehavior === undefined && action.kind === 'command'));

  const commandId: CommandDescriptorId | undefined =
    (action.commandId as CommandDescriptorId | undefined) ?? defaultCommandId;

  const availability: FeatureAvailability = action.availability ?? 'AVAILABLE';
  let available =
    availability === 'AVAILABLE' ||
    availability === 'READ_ONLY' ||
    availability === 'LOCKED_AAL';

  // P0-4: Server-authoritative authorization. The server evaluates operation
  // access via access.evaluate() before returning actions to the client, so
  // client-side permission checks are redundant. The optional hasPermission
  // runtime hook is only used when a caller explicitly provides it (e.g. for
  // testing); when absent, the server's decision is trusted.
  if (runtime?.hasPermission && action.operationId) {
    const op = CANONICAL_OPERATIONS_REGISTRY[action.operationId as string];
    const permissionId = (op?.permissionId ?? op?.permission ?? action.operationId) as string;
    available = available && runtime.hasPermission(permissionId);
  }

  if (action.requiredPermission && runtime?.hasPermission) {
    available = available && runtime.hasPermission(action.requiredPermission as string);
  }

  let visible = true;
  if (action.visibilityCondition) {
    if (!ctx) {
      visible = false;
    } else {
      visible = evaluateCondition(action.visibilityCondition, {
        ...ctx,
        data: data ?? {},
      });
    }
  }
  if (availability === 'HIDDEN' || !available) {
    visible = false;
  }

  const submitConfig = action.submitConfig;
  const validatesForm = submitConfig?.validatesForm ?? isFormSubmit;
  const includeFields = submitConfig?.includeFields ?? defaultIncludeFields;
  const excludeFields = submitConfig?.excludeFields ?? defaultExcludeFields;
  const payloadBuilderId = submitConfig?.payloadBuilderId ?? defaultPayloadBuilderId;
  const resetBehavior = submitConfig?.resetBehavior;

  const group =
    action.group ??
    (action.intent === 'danger'
      ? 'danger'
      : action.kind === 'navigate'
        ? 'navigation'
        : action.intent === 'primary'
          ? 'primary'
          : 'secondary');
  const alignment = action.alignment ?? 'end';
  const order = action.order ?? 0;

  let disabled =
    !available ||
    action.disabled === true ||
    isPending === true ||
    (formMode === 'view' && action.kind === 'command') ||
    (availability === 'READ_ONLY' && action.kind === 'command');

  // P0-2: Server-authoritative record-level action eligibility.
  // When the backend projects an `actionEligibility` map on the row data,
  // intersect it with the operation-level authorization.  If the action is
  // permission-allowed but record-level-ineligible, disable it (do not hide)
  // and surface the server-provided disabled reason rather than allowing a
  // request that predictably fails.
  let recordDisabledReasonKey: TranslationKey | undefined;
  if (!disabled && data) {
    const eligibilityMap = data.actionEligibility as RowActionEligibility | undefined;
    if (eligibilityMap) {
      const recordEligibility = eligibilityMap[action.id];
      if (recordEligibility && !recordEligibility.eligible) {
        disabled = true;
        recordDisabledReasonKey = recordEligibility.disabledReasonKey;
      }
    }
  }

  if (!disabled && runtime) {
    if (submitConfig?.requiresDirty && !runtime.isDirty) {
      disabled = true;
    }
    if (submitConfig?.requiresValid && validatesForm && runtime.isValid === false) {
      disabled = true;
    }
  }

  return {
    action,
    isFormSubmit,
    commandId,
    visible,
    disabled,
    disabledReasonKey: recordDisabledReasonKey ?? action.disabledReasonKey,
    available,
    validatesForm,
    includeFields,
    excludeFields,
    payloadBuilderId,
    resetBehavior,
    order,
    group,
    alignment,
  };
}

/**
 * Resolve a list of actions, returning only the visible ones.
 */
export function resolveActions(
  actions: ActionDescriptor[],
  options: Omit<ResolveActionOptions, 'action'>,
): ResolvedAction[] {
  return actions
    .map((action) => resolveAction({ ...options, action }))
    .filter((ra) => ra.visible);
}
